import { Component, effect, input, OnInit, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DateService } from 'src/app/services/date-service/date.service';
import { FiltersService } from 'src/app/services/filters-service/filters.service';


/**
 * Available date range filters for the graph visualization.
 * - Today: Shows data for the current day
 * - Yesterday: Shows data for the previous day
 * - Week: Shows data for the current week (Monday to Sunday)
 * - Month: Shows data for the current month
 * - Custom: Allows selecting a custom date range
 */
export enum CustomDatesCat {
  Today,
  Yesterday,
  Week,
  Month,
  Custom
}


class CustomDateFilterValues {
  today?: boolean = true
  yesterday?: boolean = true
  week?: boolean = true
  month?: boolean = true
  custom?: boolean = true
}

export interface CustomDateFilterOptions {
  default?: CustomDatesCat;
  categories?: CustomDateFilterValues
}


export interface DateFiltersValues {
  startDate: string;
  endDate: string;
}


@Component({
  selector: 'app-custom-date-filter',
  imports: [ReactiveFormsModule],
  templateUrl: './custom-date-filter.component.html',
  styleUrl: './custom-date-filter.component.scss'
})
export class CustomDateFilterComponent implements OnInit {


  options = input<CustomDateFilterOptions>({
    default: CustomDatesCat.Week,
    categories: {
      today: true,
      yesterday: true,
      week: true,
      month: true,
      custom: true
    }
  });


  dateSeletected = output<DateFiltersValues>();


  /**
   * Reference date used for navigation and period calculations
   * Defaults to today but can be changed when navigating through periods
   */
  currentDate = new Date();


  catSelected = this.options().default;


  /**
   * Form group to manage date filter inputs
   */
  public dateFilters = new FormGroup({
    startDate: new FormControl(
      this.dateService.getLocalIsosString(this.getPeriodStart()),
      Validators.required
    ),
    endDate: new FormControl(
      this.dateService.getLocalIsosString(this.getPeriodEnd()),
      Validators.required
    )
  });


  constructor(
    protected dateService: DateService,
    protected filters: FiltersService,
    protected router: Router
  ) {
    effect(() => {
      const dateFilter = this.filters.initDateFilter().get(router.url);
      this.currentDate = dateFilter ? new Date(dateFilter.startDate) : new Date();

      if (!dateFilter) return;

      // TODO: We should change the `emitEvent` param to false someday, but for
      // now it accomplished the sync between `ReportsWeeklyComponent` and the
      // rest of tabs.
      this.dateFilters.patchValue({
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate
      }, { emitEvent: true });
    });
  }

  ngOnInit(): void {
    const start = this.dateFilters.value.startDate;
    const end = this.dateFilters.value.endDate;

    this.dateSeletected.emit({
      startDate: start || '',
      endDate: end || ''
    })

    this.dateFilters.valueChanges.subscribe((values) => {
      if (values.startDate && values.endDate) {
        this.dateSeletected.emit({
          startDate: values.startDate,
          endDate: values.endDate
        })
      }
    })
  }


  // =========================================================================
  // Protected Methods
  // =========================================================================

  /**
   * Calculates the start date for the current period based on the selected filter
   * - For Custom: Uses the date from the form
   * - For Today/Yesterday: Uses the respective day at 00:00:00
   * - For Week: Finds the Monday of the current week
   * - For Month: Uses the first day of the month
   * 
   * @param date Reference date for calculations, defaults to currentDate
   * @returns Date object set to the start of the period at 00:00:00
   */
  protected getPeriodStart(date: Date = this.currentDate): Date {
    let startDate: Date;

    switch (this.catSelected) {
      case CustomDatesCat.Custom:
        startDate = new Date(this.dateFilters.get('startDate')?.value || date);
        break;

      case CustomDatesCat.Today:
        startDate = new Date();
        break;

      case CustomDatesCat.Yesterday:
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 1);
        break;

      case CustomDatesCat.Week: {
        startDate = new Date(date);
        const day = startDate.getDay();
        const diff = startDate.getDate() - day + (day === 0 ? -6 : 1);
        startDate.setDate(diff);
        break
      }

      default: // Month
        console.log(date)
        startDate = new Date(date.getFullYear(), date.getMonth(), 1);
    }

    // Set time to start of day
    startDate.setHours(0, 0, 0, 0);
    return startDate;
  }

  /**
   * Calculates the end date for the current period based on the selected filter
   * - For Custom: Uses the date from the form
   * - For Today/Yesterday: Uses the respective day at 23:59:59
   * - For Week: Adds 6 days to the start date (Sunday)
   * - For Month: Uses the last day of the month
   * 
   * @param date Reference date for calculations, defaults to currentDate
   * @returns Date object set to the end of the period at 23:59:59
   */
  protected getPeriodEnd(date: Date = this.currentDate): Date {
    let endDate: Date;

    switch (this.catSelected) {
      case CustomDatesCat.Custom:
        endDate = new Date(this.dateFilters.get('endDate')?.value || date);
        break;

      case CustomDatesCat.Today:
        endDate = new Date();
        break;

      case CustomDatesCat.Yesterday:
        endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);
        break;

      case CustomDatesCat.Week:
        endDate = new Date(this.getPeriodStart(date));
        endDate.setDate(endDate.getDate() + 6);
        break;

      default: // Month
        endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    }

    // Set time to end of day
    endDate.setHours(23, 59, 59, 999);
    return endDate;
  }

  /**
   * Updates the date filter form controls when the period changes
   * Only updates if not in Custom mode to prevent overwriting user selections
   * Sets both start and end dates according to the current period
   */
  protected updateDateFilters(): void {
    const start = this.getPeriodStart();
    const end = this.getPeriodEnd();

    this.dateFilters.patchValue({
      startDate: this.dateService.getLocalIsosString(start),
      endDate: this.dateService.getLocalIsosString(end)
    });
  }

  // =========================================================================
  // Public Methods
  // =========================================================================

  /**
   * Calculates the exact number of days between two dates, including partial days.
   * Uses millisecond precision and rounds up to ensure complete day coverage.
   * 
   * @param start - The starting date of the period
   * @param end - The ending date of the period
   * @returns The number of days between the dates, rounded up to the nearest day
   */
  /**
   * Calculates the exact number of days between two dates, including partial days.
   * Uses millisecond precision and rounds up to ensure complete day coverage.
   * 
   * @param start - The starting date of the period
   * @param end - The ending date of the period
   * @returns The number of days between the dates, rounded up to the nearest day
   */
  protected calculateDaysDifference(start: Date, end: Date): number {
    // Ensure end is always later than start
    const [earlierDate, laterDate] = start <= end ? [start, end] : [end, start];
    return Math.ceil((laterDate.getTime() - earlierDate.getTime()) / (1000 * 60 * 60 * 24));
  }

  /**
   * Calculates how many days to move forward or backward based on the current graph mode.
   * - For Custom mode: Uses the exact days between selected start and end dates
   * - For Week mode: Returns 7 days
   * - For Month mode: Calculates exact days in the month
   * 
   * @param direction - Whether to calculate offset for moving forward ('next') or backward ('previous')
   * @returns The number of days to offset. Positive for forward, negative for backward. Returns 0 if calculation fails.
   */
  /**
   * Gets the number of days to offset when navigating between periods
   * 
   * @param direction - Whether to move forward or backward
   * @returns Number of days to offset (positive for next, negative for previous)
   */
  protected getPeriodOffset(direction: 'next' | 'previous'): number {
    const multiplier = direction === 'next' ? 1 : -1;

    switch (this.catSelected) {
      case CustomDatesCat.Today:
        return 1 * multiplier;
      case CustomDatesCat.Yesterday:
        return 1 * multiplier;
      case CustomDatesCat.Week:
        return 7 * multiplier;
      case CustomDatesCat.Month: {
        const currentDate = new Date(this.currentDate);
        const nextDate = new Date(currentDate);
        nextDate.setMonth(nextDate.getMonth() + multiplier);
        return multiplier * this.calculateDaysDifference(
          multiplier > 0 ? currentDate : nextDate,
          multiplier > 0 ? nextDate : currentDate
        );
      }

      case CustomDatesCat.Custom: {
        const start = new Date(this.dateFilters.get('startDate')?.value || '');
        const end = new Date(this.dateFilters.get('endDate')?.value || '');
        if (!start || !end) return 0;
        return this.calculateDaysDifference(start, end) * multiplier;
      }

      default:
        return 0;
    }
  }

  /**
   * Navigates to the previous time period based on the current graph mode.
   * - For Custom: Moves back by the exact number of days in the selected range
   * - For Week: Moves back 7 days
   * - For Month: Moves back the exact number of days in the previous month
   * 
   * After moving, updates the date filters to reflect the new period.
   * If the offset calculation fails (e.g., invalid dates), no navigation occurs.
   */
  previousPeriod(): void {
    const offset = this.getPeriodOffset('previous');
    if (offset === 0) return;

    if (this.catSelected === CustomDatesCat.Custom) {
      const startDate = new Date(this.dateFilters.get('startDate')?.value || '');
      const endDate = new Date(this.dateFilters.get('endDate')?.value || '');

      startDate.setDate(startDate.getDate() + offset);
      endDate.setDate(endDate.getDate() + offset);

      this.dateFilters.patchValue({
        startDate: this.dateService.getLocalIsosString(startDate),
        endDate: this.dateService.getLocalIsosString(endDate)
      });
    } else {
      if (this.catSelected === CustomDatesCat.Today || this.catSelected === CustomDatesCat.Yesterday) {
        // Crear nueva fecha basada en la fecha actual del sistema
        const newDate = new Date();
        // Si estamos en "Today", retrocedemos un día desde hoy
        // Si estamos en "Yesterday", retrocedemos dos días desde hoy
        const daysToSubtract = this.catSelected === CustomDatesCat.Today ? 1 : 2;
        newDate.setDate(newDate.getDate() - daysToSubtract);

        const startOfDay = new Date(newDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(newDate);
        endOfDay.setHours(23, 59, 59, 999);

        this.dateFilters.patchValue({
          startDate: this.dateService.getLocalIsosString(startOfDay),
          endDate: this.dateService.getLocalIsosString(endOfDay)
        });

        this.currentDate = newDate;
        this.catSelected = CustomDatesCat.Custom;
      } else {
        const newDate = new Date(this.currentDate);
        newDate.setDate(newDate.getDate() + offset);
        this.currentDate = newDate;
        this.updateDateFilters();
      }
    }
  }

  /**
   * Navigates to the next time period based on the current graph mode.
   * - For Custom: Moves forward by the exact number of days in the selected range
   * - For Week: Moves forward 7 days
   * - For Month: Moves forward the exact number of days in the next month
   * 
   * After moving, updates the date filters to reflect the new period.
   * If the offset calculation fails (e.g., invalid dates), no navigation occurs.
   */
  nextPeriod(): void {
    const offset = this.getPeriodOffset('next');
    if (offset === 0) return;

    if (this.catSelected === CustomDatesCat.Custom) {
      const startDate = new Date(this.dateFilters.get('startDate')?.value || '');
      const endDate = new Date(this.dateFilters.get('endDate')?.value || '');

      startDate.setDate(startDate.getDate() + offset);
      endDate.setDate(endDate.getDate() + offset);

      this.dateFilters.patchValue({
        startDate: this.dateService.getLocalIsosString(startDate),
        endDate: this.dateService.getLocalIsosString(endDate)
      });
    } else {
      if (this.catSelected === CustomDatesCat.Today || this.catSelected === CustomDatesCat.Yesterday) {
        // Crear nueva fecha basada en la fecha actual del sistema
        const newDate = new Date();
        // Si estamos en "Today", avanzamos un día desde hoy
        // Si estamos en "Yesterday", no sumamos días (nos movemos a hoy)
        const daysToAdd = this.catSelected === CustomDatesCat.Today ? 1 : 0;
        newDate.setDate(newDate.getDate() + daysToAdd);

        const startOfDay = new Date(newDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(newDate);
        endOfDay.setHours(23, 59, 59, 999);

        this.dateFilters.patchValue({
          startDate: this.dateService.getLocalIsosString(startOfDay),
          endDate: this.dateService.getLocalIsosString(endOfDay)
        });

        this.currentDate = newDate;
        this.catSelected = CustomDatesCat.Custom;
      } else {
        const newDate = new Date(this.currentDate);
        newDate.setDate(newDate.getDate() + offset);
        this.currentDate = newDate;
        this.updateDateFilters();
      }
    }
  }
}
